import type { BiomeId } from '@/styles/theme'
import type { LevelMeta } from './levels'

export interface StoryBeat {
  id: string
  title: string
  subtitle: string
  image: string
}

export interface BiomeBriefing {
  title: string
  lines: string[]
}

export interface LevelStoryCue {
  id: string
  title: string
  line: string
  tone: 'normal' | 'excited' | 'warning' | 'achievement'
}

function art(name: string): string {
  return `/art/${name}.webp`
}

export const BIOME_BRIEFINGS: Record<BiomeId, BiomeBriefing> = {
  forest: {
    title: 'The Whispering Forest',
    lines: [
      'A gust tore Ember from the hearth and dropped it among the moss.',
      'Home is a lit window somewhere past the pines.',
      'Slide until something stops you. The forest is forgiving. For now.',
    ],
  },
  cave: {
    title: 'The Echoing Caves',
    lines: [
      'The path home dips underground, where echoes outnumber footsteps.',
      'Keys rust in the dark and doors forget their manners.',
      'Momentum is your only lantern down here.',
    ],
  },
  lava: {
    title: 'The Smoldering Fields',
    lines: [
      'The mountain is awake and it did not sleep well.',
      'One wrong slide ends in the melt. Ember knows fire, but not like this.',
      'Read the whole route before you commit to it.',
    ],
  },
  ice: {
    title: 'The Glass Tundra',
    lines: [
      'Everything here keeps moving after you stop wanting it to.',
      'Ice carries you past your plans and off your maps.',
      'Home is close enough to frost the window now.',
    ],
  },
  magical: {
    title: 'The Shimmerlands',
    lines: [
      'Space folds politely here; teleporters hum old songs.',
      'Switches whisper to every door at once.',
      'Trust the geometry, not your eyes.',
    ],
  },
  dungeon: {
    title: 'The Last Dungeon',
    lines: [
      'The final stretch. Stone that has swallowed a thousand travelers.',
      'Every mechanic you have met waits in these halls, all at once.',
      'The window is lit at the end. Take Ember home.',
    ],
  },
}

export const HEARTH_BEATS: Record<BiomeId, StoryBeat> = {
  forest: {
    id: 'hearth_grove',
    title: 'The Grove Hearth wakes',
    subtitle:
      'At the forest’s edge slept an old green fire. Ember gave a little of its own warmth, and the path down was lit.',
    image: art('story_hearth_grove'),
  },
  cave: {
    id: 'hearth_deepcoal',
    title: 'The Deep Coal',
    subtitle: 'One old coal still burned in the black, kept alight so no traveler would pass through alone.',
    image: art('story_hearth_deepcoal'),
  },
  lava: {
    id: 'hearth_oldmountain',
    title: 'The Old Mountain',
    subtitle: 'A slumbering mountain-hearth with molten fire in its chest. A fire not Ember’s kind, but kin.',
    image: art('story_hearth_oldmountain'),
  },
  ice: {
    id: 'hearth_frostlantern',
    title: 'The Frost Lantern',
    subtitle: 'A steady candle-flame in clear ice, throwing aurora light. The clearest foreshadow of Home.',
    image: art('story_hearth_frostlantern'),
  },
  magical: {
    id: 'hearth_shimmer',
    title: 'The Shimmer Hearth',
    subtitle: 'A hearth of folded, shifting light. It teaches trust in the order beneath appearances.',
    image: art('story_hearth_shimmer'),
  },
  dungeon: {
    id: 'homecoming',
    title: 'Homecoming',
    subtitle:
      'At the end of the last dark hall, a window was lit. Ember slid home, brighter than it left, and every freed spark turned toward a light of its own.',
    image: art('story_homecoming'),
  },
}

export function hearthBeat(biome: BiomeId): StoryBeat {
  return HEARTH_BEATS[biome]
}

const BIOME_LEVEL_STAKES: Record<BiomeId, string> = {
  forest: 'A green path opens between Ember and the far home window.',
  cave: 'The dark presses close around Ember, but the route home still answers to momentum.',
  lava: 'Heat bends the air; one clean route keeps Ember moving toward home instead of the melt.',
  ice: 'The frost keeps carrying Ember beyond every half-plan while home glows through the cold.',
  magical: 'The Shimmerlands rearrange distance around Ember, but not the promise of home.',
  dungeon: 'The last halls tighten around Ember and the final home light.',
}

const MECHANIC_STAKES: { tile: string; line: string; tone: LevelStoryCue['tone'] }[] = [
  { tile: 'k', line: 'A key waits somewhere on the route, and the locked way home will not open without it.', tone: 'warning' },
  { tile: 'd', line: 'A sealed door turns the level into a rescue path, not just a race.', tone: 'warning' },
  { tile: 't', line: 'A teleporter folds the path, asking Ember to trust the jump between sparks.', tone: 'excited' },
  { tile: 's', line: 'Ice strips away hesitation; every slide needs a place to land.', tone: 'warning' },
  { tile: 'l', line: 'Lava makes the mistake visible before it is forgiven.', tone: 'warning' },
  { tile: 'b', line: 'Brittle stone can break the route open if Ember spends the impact wisely.', tone: 'normal' },
  { tile: '~', line: 'Water dims the glow, so every dry stop matters.', tone: 'warning' },
  { tile: 'm', line: 'A mirror turn changes the route without changing the destination.', tone: 'excited' },
  { tile: 'w', line: 'Wind pushes the journey sideways unless the route accounts for it.', tone: 'warning' },
]

function countTiles(grid: string[], tile: string): number {
  return grid.reduce((sum, row) => sum + [...row].filter((cell) => cell === tile).length, 0)
}

function mechanicStake(level: LevelMeta): { line: string; tone: LevelStoryCue['tone'] } {
  const grid = level.grid.join('')
  const found = MECHANIC_STAKES.find((stake) => grid.includes(stake.tile))
  if (found) return found
  if (level.gemCount > 0) return { line: 'A brighter spark is hidden off the straightest route for players who can spare the slide.', tone: 'achievement' }
  if (level.coinCount > 0) return { line: 'Loose warmth is scattered along the way, enough to make the trip home brighter.', tone: 'achievement' }
  return { line: 'The route is bare and honest: read the walls, then carry Ember home.', tone: 'normal' }
}

export function getLevelStoryCue(level: LevelMeta): LevelStoryCue {
  const briefing = BIOME_BRIEFINGS[level.biome]
  const mechanic = mechanicStake(level)
  const tightRoute = level.stepTarget - level.minSlides <= 1
  const collectibleCount = level.coinCount + level.gemCount
  const pressure =
    tightRoute && level.difficulty !== 'easy'
      ? ' There is little room between the best path and a tired one.'
      : collectibleCount >= 4
        ? ` ${collectibleCount} stray sparks can be carried back if the route allows it.`
        : ''

  return {
    id: `${level.index}-${level.biome}-${level.difficulty}-${countTiles(level.grid, 'e')}`,
    title: briefing.title,
    line: `${BIOME_LEVEL_STAKES[level.biome]} ${mechanic.line}${pressure}`,
    tone: mechanic.tone,
  }
}
